import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ClipboardList,
  RefreshCw,
} from 'lucide-react';
import { reservationService } from '../../../../services/reservationService';
import { StcReservation } from '../../../../types/reservation';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { WarehouseTable } from '../../../shared/table/components/WarehouseTable';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation } from '../../../shared/table/types/table';

// Helper function to format date for display
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to format numbers with thousand separator
const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const columnFactory = createColumnFactory<StcReservation>();

interface ReservationsAppProps {
  onBack?: () => void;
}

export const ReservationsApp: React.FC<ReservationsAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // State management
  const [reservations, setReservations] = useState<StcReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load reservations
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reservationsData = await reservationService.getActiveReservations();
      setReservations(reservationsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.reservations.loadError', {}, 'En ukjent feil oppstod'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Column definitions
  const columns: ColumnDefinition<StcReservation>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 80,
      sortable: true,
      filterType: 'dropdown',
    }),
    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 120,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('warehouse.general.itemNumber', {}, 'Varenummer'), {
      width: 130,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'text',
      formatter: (value) => value || '-',
    }),
    columnFactory.createNumberColumn('resQuantity', 'resQuantity', t('manager.modules.reservations.columns.resQuantity', {}, 'Ant. reservert'), {
      width: 120,
      sortable: true,
      alignment: 'right',
      formatter: (value, row) => {
        const qty = formatNumber(value as number);
        return row?.itemUnitCode ? `${qty} ${row.itemUnitCode}` : qty;
      },
    }),
    columnFactory.createDateColumn('resDate', 'resDate', t('manager.modules.reservations.columns.resDate', {}, 'Res. dato'), {
      width: 140,
      sortable: true,
      formatter: (value) => formatDateTime(value as string),
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.reservations.columns.orderNoErp', {}, 'Ordre (ERP)'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createTextColumn('orderLineERP', 'orderLineERP', t('manager.modules.reservations.columns.orderLineErp', {}, 'Linje (ERP)'), {
      width: 90,
      sortable: true,
      visible: true,
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('orderName', 'orderName', t('manager.modules.reservations.columns.customer', {}, 'Kunde'), {
      width: 160,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.reservations.columns.pickList', {}, 'Plukkliste'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', t('manager.modules.reservations.columns.grShipment', {}, 'GR Shipment'), {
      width: 110,
      sortable: true,
      visible: false,
      formatter: (value) => value != null ? String(value) : '-',
    }),
  ], [t]);

  // Summary calculations
  const summaryCalculations: SummaryCalculation<StcReservation>[] = useMemo(() => [
    {
      column: 'resQuantity' as keyof StcReservation,
      type: 'sum' as const,
      formatter: (value: number) => formatNumber(value),
    },
    {
      column: 'itemNo' as keyof StcReservation,
      type: 'count' as const,
      formatter: (value: number) => t('manager.modules.reservations.summaryCount', { '0': value }, `${value} reservasjoner`),
    },
  ], [t]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadData(),
      variant: 'secondary' as const,
    },
  ], [loadData, t]);

  // Row ID extractor
  const getRowId = useCallback((row: StcReservation) => row.resID, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList size={32} className="text-teal-600" />
                {t('manager.modules.reservations.title', {}, 'Reservasjoner')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('manager.modules.reservations.subtitle', {}, 'Oversikt over aktive varereservasjoner i lageret')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* WarehouseTable */}
      <div className="flex-1 px-6 pb-6 min-h-0">
        <WarehouseTable<StcReservation>
          data={reservations}
          columns={columns}
          getRowId={getRowId}
          config={{
            persistenceKey: 'reservations-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            stickyFooter: true,
            compactMode: true,
          }}
          summaryCalculations={summaryCalculations}
          tableActions={tableActions}
          loading={loading}
          error={error || undefined}
          aria-label={t('manager.modules.reservations.tableAria', {}, 'Reservasjoner tabell')}
        />
      </div>
    </div>
  );
};
